import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Alert,
	Divider,
	Flex,
	Grid,
	Input,
	Space,
	Switch,
	Tooltip,
} from 'antd';
import {
	AccountTypes,
	BankAccountTypes,
	CalculationMethodForExpensesSubjectToHireDays,
	FixtureTypes,
	FixtureTypeLabels,
	PaymentTerms,
	PaymentTermLabels,
	HireUnit,
	VesselOwnershipTypes,
	BareboatItemType,
	Currencies,
	ContractNamingConventions,
	FuelTypes,
	TcInAllocationItemTypes,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { capitalize } from '@shared/utils/string';
import { Values } from '@shared/utils/objectEnums';
import { formatPercentage } from '@shared/utils/formatPercentage';
import { filterUnique } from '@shared/utils/array';
import isBankAccountAvailableForFixingCompany from '@shared/utils/isBankAccountAvailableForFixingCompany';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import type { UpdateBbItemRequest } from '@api/features/fixtures/updateBbItem';
import type { CreateBbItemRequest } from '@api/features/fixtures/createBbItem';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import type { Port } from '@api/utils/ports';
import {
	getAddressCommissionField,
	getBankAccountField,
	getChartererField,
	getFixingCompanyField,
	getCpDateField,
	getCpFormField,
	getIdentifierField,
	getLaycanFromField,
	getLaycanToField,
	getNotesField,
	getVesselCosts,
	getVesselField,
} from '@client/screens/fixtures/shared/sectionItems';
import { getPortOptions } from '@client/utils/getPortAndRangeOptions';
import CVEFormulaLabel from '@client/components/CVEFormulaLabel';
import {
	getVessels,
	getPortsAndRanges,
	updateFixture,
	getVesselBankAccounts,
	getOrgBankAccounts,
	getReportGroups,
	updateBbItem,
	createBbItem,
	deleteBbItem,
	getUserData,
	renameIdentifier,
	getVoyages,
	updateTcInAllocationItem,
} from '@client/lib/api';
import BunkersTable from '@client/components/BunkersTable/BunkersTable';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import EditableTable from '@client/components/EditableTable';
import TooltipIcon from '@client/components/TooltipIcon';
import NumericInput from '@client/components/NumericInput';
import Select from '@client/components/Select';
import styles from './styles/useTcSectionItems.module.css';
import PeriodScheduleTable from './PeriodScheduleTable';
import BrokersTable from './BrokersTable';
import CveTable from './CveTable';
import RevenueItemsTable from './RevenueItemsTable';
import TcExpensesTables from './TcExpensesTables';

type Props = {
	fixture: GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps> | undefined;
	fixtureLoading: boolean;
	refreshFixture: () => Promise<void> | void;
	charterers: GetCounterpartiesResponse | undefined;
};

type BbItemsFromFixtureDetails = GetFixtureDetailsResponse<BbFixtureProps>['BbItems'];

export const useTcSectionItems = ({
	fixture: incomingFixture,
	fixtureLoading,
	refreshFixture,
	charterers,
}: Props) => {
	const [fixture, setFixture] = useState<
		GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps> | undefined
	>(incomingFixture);
	const [tcTerms, setTcTerms] = useState<TcTermsWithIncludes | undefined>(incomingFixture?.tcTerms);

	const [portsAndRanges] = useFetchedState(getPortsAndRanges);
	const [userInfo] = useFetchedState(getUserData);
	const [vessels] = useFetchedState(getVessels);
	const [reportGroups] = useFetchedState(getReportGroups);
	const screens = Grid.useBreakpoint();

	const [allVoyages] = useFetchedState(() => getVoyages({
		disablePagination: true,
		includeCompleted: true,
	}));

	const linkedOutVoyages = useMemo(() => (
		allVoyages?.rows.filter((voyage) => (
			voyage.linkedTcInVoyageId === fixture?.voyageId
		)) ?? []
	), [allVoyages, fixture?.voyageId]);
	
	const grossBallastBonusAllocation = fixture?.tcInAllocationItems.find((item) => (
		item.itemType === TcInAllocationItemTypes.GROSS_BALLAST_BONUS
	));

	const availableVessels = useMemo(() => {
		return (vessels ?? [])
			.sort((a, b) => a.name.toString().localeCompare(b.name))
			.filter((v) => {
				if (fixture?.type === VesselOwnershipTypes.TC_IN) {
					return v.ownershipType === VesselOwnershipTypes.TC_IN;
				}

				return true;
			});
	}, [vessels, fixture?.type]);

	const firstDeliveryPort = useMemo(() => {
		const ports = tcTerms?.deliveryPortsOrRanges ?? [];

		return ports.find((p) => p?.isPort) ?? ports[0] ?? null;
	}, [tcTerms?.deliveryPortsOrRanges]);

	useEffect(() => {
		setFixture(incomingFixture);
		setTcTerms(incomingFixture?.tcTerms);
	}, [incomingFixture]);

	const [bankAccounts] = useFetchedState(async () => {
		if (fixture?.vesselId == null || vessels == null || vessels.length === 0) {
			return [];
		}

		const bankAccountType = fixture.type === FixtureTypes.TC_IN ?
			BankAccountTypes.TC_IN :
			BankAccountTypes.OURS;

		const vesselAccounts = await getVesselBankAccounts(fixture.vesselId, bankAccountType);
		const orgAccounts = await getOrgBankAccounts(true);
		const accounts = [...vesselAccounts, ...orgAccounts];

		return fixture.type === FixtureTypes.TC_IN ?
			accounts :
			accounts.filter((a) => isBankAccountAvailableForFixingCompany(a, fixture.fixingCompanyId));
	}, [fixture, vessels]);

	const portOptions = useMemo(
		() => getPortOptions((portsAndRanges ?? []).filter((p): p is Port => p.isPort === true)),
		[portsAndRanges],
	);

	const fuelGrades = useMemo(
		() => {
			if (fixture?.vessel) {
				const allConsumptionSets = fixture.vessel.ConsumptionSets;
				const allPerformanceEntries = allConsumptionSets
					.flatMap((c) => c.PerformanceEntries)
					.map((p) => p.fuelType);

				fixture.vessel.PerformanceEntries.forEach((p) => allPerformanceEntries.push(p.fuelType));

				return filterUnique(allPerformanceEntries, (p) => p);
			}

			return Object.keys(FuelTypes) as Array<Values<typeof FuelTypes>>;
		},
		[fixture?.vessel],
	);

	const selectedBankAccount = useMemo(() => {
		if (bankAccounts == null) {
			return null;
		}

		return bankAccounts.find((a) => a.id === fixture?.bankAccountId);
	}, [bankAccounts, fixture]);

	const fixtureCurrency = selectedBankAccount == null ?
		userInfo?.baseCurrency ?? Currencies.USD :
		selectedBankAccount.currency;

	if (fixtureLoading || fixture?.id == null || tcTerms == null) {
		return [];
	}

	const allowEditKeyTerms = !fixture?.hasHireInvoices;

	const handleCreateBbItem = async (
		values: CreateBbItemRequest['attributes'],
	) => {
		const brokerPaidByOwner = values.brokerPaidByOwnerIds?.map(
			(id) => brokersOptions.find((broker) => broker.value === id),
		);
		const brokerPaidByCharterer = values.brokerPaidByChartererIds?.map(
			(id) => brokersOptions.find((broker) => broker.value === id),
		);

		const updatedValues = {
			...values,
			brokerPaidByOwnerPercentages: brokerPaidByOwner ?
				brokerPaidByOwner.map((broker) => (broker ? broker.commission : null)) :
				[],
			brokerPaidByChartererPercentages: brokerPaidByCharterer ?
				brokerPaidByCharterer.map((broker) => (broker ? broker.commission : null)) :
				[],
		};

		await createBbItem({ fixtureId: fixture.id, attributes: updatedValues });
		await refreshFixture();
	};

	const handleUpdateBbItem = async (
		bbItemId: number,
		values: UpdateBbItemRequest['attributes'],
	) => {
		await updateBbItem({ fixtureId: fixture.id, bbItemId, attributes: values });
		await refreshFixture();
	};

	const handleDeleteBbItem = async (bbItemId: number) => {
		await deleteBbItem({ fixtureId: fixture.id, bbItemId });
		await refreshFixture();
	};

	const getRedeliveryStr = (): string => {
		let identifier = '';

		if (
			fixture.prevVoyage?.identifier != null &&
			fixture.prevFixture?.tcTerms?.FixtureCounterparty?.name != null
		) {
			identifier = `(${fixture.prevVoyage?.identifier} with ${fixture.prevFixture?.tcTerms?.FixtureCounterparty?.name})`;
		}

		return (
			`The vessel's previous ${fixture.prevFixture != null && FixtureTypeLabels[fixture.prevFixture.type]} 
			voyage ${identifier} redelivered with the following bunkers:`
		);
	};

	const itemTypeOptions = Object.values(BareboatItemType).map((value) => ({ label: value, value }));

	const brokersOptions = fixture.brokers.map((broker) => (
		{
			label: broker.name,
			value: broker.brokerId,
			commission: broker.commission,
			paidBy: broker.paidBy,
			id: broker.id,
		}));

	const onRegenerateIdentifer = async () => {
		await renameIdentifier(fixture.id);
		await refreshFixture();
	};

	const paymentTermOptions = [
		{
			label: PaymentTermLabels[PaymentTerms.PERIOD_START],
			value: PaymentTerms.PERIOD_START,
		},
		{
			label: PaymentTermLabels[PaymentTerms.PERIOD_END],
			value: PaymentTerms.PERIOD_END,
		},
		{
			label: PaymentTermLabels[PaymentTerms.INVOICE_DATE],
			value: PaymentTerms.INVOICE_DATE,
		},
	];

	return [
		{
			title: 'Key Terms',
			fields: [
				getChartererField({ fixture, required: true, charterers }),
				getFixingCompanyField({ fixture, fixingCompanies: userInfo?.fixingCompanies ?? [] }),
				getIdentifierField({
					fixture,
					namingConvention: userInfo?.contractNamingConvention ?? ContractNamingConventions.LEGACY,
					onRegenerate: onRegenerateIdentifer,
				}),
				...(fixture.type === FixtureTypes.TC_OUT ?
					[
						getVesselCosts({ fixture }),
					] :
					[]),
				getVesselField({ fixture, vessels: availableVessels ?? [], required: true }),
				getBankAccountField({ fixture, bankAccounts: bankAccounts ?? [], required: true }),
			],
		},
		{
			title: 'Broker Commissions',
			fields: [
				...(fixture.type === FixtureTypes.BB_OUT ?
					[] :
					[
						getAddressCommissionField({ fixture, allowEditKeyTerms, required: false }),
					]),
			],
			content: (
				<>
					<br />
					<BrokersTable 
						refreshFixture={refreshFixture} 
						fixtureDetails={fixture} 
						tcTerms={fixture.tcTerms}
					/>
				</>
			),
		},
		{
			title: 'CP',
			fields: [
				getCpDateField({ fixture, required: false }),
				getCpFormField({ fixture, required: false }),
			],
		},
		{
			title: 'Ports & Laycan',
			fields: [
				{
					key: 'deliveryPortsOrRanges',
					label: 'Delivery Port',
					value: tcTerms.deliveryPortsOrRanges?.[0]?.id ?? null,
					editable: true,
					required: false,
					type: 'select',
					options: portOptions,
					transformResult: (port: number | null) => ({
						deliveryPortsOrRanges: port == null ? [] : [port],
					}),
					inputProps: {
						showSearch: true,
						allowClear: true,
					},
					span: 3,
				},
				{
					key: 'redeliveryPortsOrRanges',
					label: 'Redelivery Port',
					value: tcTerms.redeliveryPortsOrRanges?.[0]?.id ?? null,
					editable: true,
					required: false,
					type: 'select',
					options: portOptions,
					transformResult: (port: number | null) => ({
						redeliveryPortsOrRanges: port == null ? [] : [port],
					}),
					inputProps: {
						showSearch: true,
						allowClear: true,
					},
					span: 3,
				},
				{
					key: 'deliveryRange',
					label: 'Delivery Range',
					value: tcTerms.deliveryRange,
					editable: true,
					required: false,
					span: 3,
				},
				{
					key: 'redeliveryRange',
					label: 'Redelivery Range',
					value: tcTerms.redeliveryRange,
					editable: true,
					required: false,
					span: 3,
				},
				getLaycanFromField({ fixture, firstDeliveryPort }),
				getLaycanToField({ fixture, firstDeliveryPort }),
			],
		},
		...(fixture.type === FixtureTypes.BB_OUT ? [{
			title: 'Capex, Opex & other',
			content: (
				<>
					<br />
					<EditableTable<BbItemsFromFixtureDetails[number], 'id'>
						iconButtons
						pagination={false}
						enableDelete={allowEditKeyTerms}
						enableEdit
						allowAddNew
						addNewText="Item(s)"
						emptyText="No items"
						size="small"
						dataSource={fixture.BbItems ?? []}
						onSave={handleUpdateBbItem}
						onAddNew={handleCreateBbItem}
						onDelete={handleDeleteBbItem}
						editingRowClassName={styles.editingBroker}
						keyDataIndex="id"
						useCards={screens.xs}
						columns={[
							{
								dataIndex: 'type',
								title: 'Type',
								width: 130,
								editable: true,
								render: (value: BareboatItemType) => {
									const option = itemTypeOptions.find((type) => type.label === value);

									return option ? option.label : value;
								},
								editingProps: {
									type: 'select',
									inputProps: {},
									options: (() => {
										const usedTypes = (fixture.BbItems ?? []).map((item) => item.type);

										return itemTypeOptions.filter(
											(option) => !usedTypes.includes(option.value),
										);
									})(),
								},
							},
							{
								dataIndex: 'amount',
								title: 'Amount',
								width: 215,
								editable: true,
								editingProps: {
									type: 'currency',
									inputProps: {
										currency: fixture.bankAccount?.currency ?? Currencies.USD,
										addonAfter: '/ day',
									},
								},
								render: (i: number) => {
									return formatCurrency(i ?? 0, fixture.bankAccount ?
										fixture.bankAccount.currency : Currencies.USD);
								},
							},
							{
								dataIndex: 'brokerPaidByOwnerIds',
								title: 'Broker comm paid by owner',
								width: 230,
								editable: true,
								render: (value: number[]) => {
									// Ensure `brokerPaidByOwnerId` is an array
									const brokerIds = Array.isArray(value) ? value : [value];

									// Find matching brokers for each selected ID
									const brokersOwner = brokerIds
										.map((brokerId) => {
											const option = brokersOptions.find(
												(brokerOption) => brokerOption.value === brokerId,
											);

											return option ? `${option.label} (${option.commission}%)` : null;
										})
										.filter((label) => label != null);

									// Render each broker on a new line
									return brokersOwner.length > 0 ?
										brokersOwner.map((broker, idx) => (<div key={idx}>{broker}</div>)) :
										'None';
								},
								editingProps: {
									type: 'select',
									inputProps: {
										mode: 'multiple',
									},
									options: [
										{
											label: 'Brokers',
											options: brokersOptions.filter(
												(broker) => broker.paidBy === AccountTypes.OWNER,
											),
										},
									],
								},
							},
							{
								dataIndex: 'brokerPaidByChartererIds',
								title: 'Broker comm paid by charterer',
								width: 250,
								editable: true,
								render: (value: number[]) => {
									// Ensure `brokerPaidByOwnerId` is an array
									const brokerIds = Array.isArray(value) ? value : [value];

									// Find matching brokers for each selected ID
									const brokersOwner = brokerIds
										.map((brokerId) => {
											const option = brokersOptions.find(
												(brokerOption) => brokerOption.value === brokerId,
											);

											return option ? `${option.label} (${option.commission}%)` : null;
										})
										.filter((label) => label != null);

									// Render each broker on a new line
									return brokersOwner.length > 0 ?
										brokersOwner.map((broker, idx) => (<div key={idx}>{broker}</div>)) :
										'None';
								},
								editingProps: {
									type: 'select',
									inputProps: {
										mode: 'multiple',
									},
									options: [
										{
											label: 'Brokers',
											options: brokersOptions.filter(
												(broker) => broker.paidBy === AccountTypes.CHARTERER,
											),
										},
									],
								},
							},
							{
								dataIndex: 'addComm',
								title: 'Ad. Commissions',
								width: 150,
								editable: true,
								editingProps: {
									type: 'percentage',
									inputProps: {},
								},
								render: (c: number) => c != null && formatPercentage(c),
							},
							{
								dataIndex: 'pcm',
								title: 'PCM Invoicing',
								width: 150,
								editable: true,
								editingProps: {
									inputProps: {},
									type: 'select',
									options: [
										{
											label: 'Enabled',
											value: true,
										},
										{
											label: 'Disabled',
											value: false,
										},
									],
								},
								render: (b: boolean) => (b ? 'Enabled' : 'Disabled'),
							},
						]}
					/>
				</>
			),
		}] : []),
		{
			title: 'Hire Settings',
			fields: [
				...(fixture.type !== FixtureTypes.BB_OUT ? [
					{
						key: 'hireUnit',
						label: 'Hire Units',
						type: 'select',
						value: tcTerms.hireUnit === HireUnit.MONTHS ? ' Per month ' : ' Per day ',
						options: Object.values(HireUnit).map((value) => ({
							label: fixture.fixed ? (
								<Tooltip title="Cannot edit this field when estimate is fixed">
									{capitalize(value)}
								</Tooltip>
							) : capitalize(value),
							value,
						})),
						editable: !fixture.fixed,
						required: true,
						inputProps: {
							showSearch: true,
						},
					},
					{
						key: 'paymentTermValue',
						label: 'Payment Terms',
						value: tcTerms.paymentTermValue,
						editable: true,
						renderInput: ({ value, onChange }: { value: string; onChange: () => void }) => (
							<Space>
								<NumericInput
									style={{ width: 50 }}
									value={Number(value)}
									onChange={onChange}
								/>
								<div>Days from</div>
								<Select
									style={{ width: 250 }}
									allowClear={false}
									value={tcTerms.paymentTerm}
									onChange={(val: string | null) => {
										if (val != null) {
											updateFixture(fixture.id, {
												'paymentTerm': val,
											}).then(refreshFixture);
										}
									}}
									options={paymentTermOptions}
									placeholder="Select term"
								/>
							</Space>
						),
					},
					{
						key: 'grossBallastBonus',
						label: 'Gross Ballast Bonus',
						type: 'currency',
						value: tcTerms.grossBallastBonus,
						render: (i: { value: number }) => formatCurrency(i.value || 0, fixtureCurrency),
						inputProps: {
							placeholder: 'No Bonus',
							returnNullOnEmpty: true,
							currency: fixtureCurrency,
						},
						editable: allowEditKeyTerms,
					},
					...(fixture.type === FixtureTypes.TC_IN ? [{
						key: 'grossBallastBonusAllocation',
						label: 'Allocate Gross Ballast Bonus to voyage',
						value: grossBallastBonusAllocation?.allocatedToId,
						editable: true,
						renderInput: () => (
							<Select
								allowClear
								placeholder="Select voyage"
								value={grossBallastBonusAllocation?.allocatedToId ?? undefined}
								status={grossBallastBonusAllocation?.allocatedToId == null ?
									'error' :
									undefined}
								options={linkedOutVoyages.map((voyage) => ({
									value: voyage.id,
									label: `${voyage.identifier} - ${FixtureTypeLabels[voyage.fixtureType]}`,
								}))}
								onChange={async (newVoyageId: number | null) => {
									const currentVoyageId = grossBallastBonusAllocation?.allocatedToId;

									if (newVoyageId != null) {
										await updateTcInAllocationItem(
											newVoyageId,
											0,
											TcInAllocationItemTypes.GROSS_BALLAST_BONUS,
											100,
										);
									} else if (currentVoyageId != null) {
										await updateTcInAllocationItem(
											currentVoyageId,
											0,
											TcInAllocationItemTypes.GROSS_BALLAST_BONUS,
											0,
										);
									}

									await refreshFixture();
								}}
							/>
						),
					}] : []),
					{
						key: 'useUTC',
						label: 'UTC / Local time',
						type: 'select',
						value: tcTerms.useUTC,
						editable: true,
						renderInput: ({ value, onChange }: { value: number; onChange: () => void }) => {
							if ('hireUnit' in fixture && fixture.hireUnit === HireUnit.MONTHS) {
								return (
									<Tooltip title="Cannot edit this field when hire unit is set to Months">
										<Select
											value=" - "
											disabled
										/>
									</Tooltip>
								);
							}

							return (
								<Select
									value={value}
									onChange={onChange}
									options={[
										{ label: 'UTC', value: true },
										{ label: 'Local time', value: false },
									]}
								/>
							);
						},
					},
					{
						key: 'hireDescription',
						label: 'Hire Description',
						value: tcTerms.hireDescription,
						type: 'textarea',
						editable: allowEditKeyTerms,
					},
				] : []),
				{
					key: 'hireHeader',
					label: (<b>Days per hire period</b>),
					render: () => null,
				},
				...(fixture.type !== FixtureTypes.BB_OUT ? [
					{
						key: 'invoicePerCalenderMonth',
						label: '— Full calendar month (PCM)',
						type: 'switch',
						renderInput: ({ value, onChange }: { value: boolean; onChange: () => void }) => {
							return (
								<Tooltip title={`${tcTerms.hireUnit === HireUnit.MONTHS ? 
									'Cannot edit this field when hire unit is set to Months' : 
									''}`}>
									<Switch
										checked={value || false}
										disabled={tcTerms.hireUnit === HireUnit.MONTHS}
										onChange={onChange}
									/>
								</Tooltip>
							);
						},
						value: tcTerms.invoicePerCalenderMonth,
						editable: allowEditKeyTerms,
					},
					{
						key: 'daysPerHirePeriod',
						label: '— Or enter manually',
						value: tcTerms.daysPerHirePeriod,
						type: 'number',
						renderInput: ({ value, onChange }: { value: number; onChange: () => void }) => {
							if (tcTerms.invoicePerCalenderMonth || tcTerms.hireUnit === HireUnit.MONTHS) {
								return (
									<Tooltip title="Cannot edit this field when PCM is on or hire unit is set to Months">
										<Input
											value=""
											disabled
											addonAfter="Days"
										/>
									</Tooltip>
								);
							}

							return (
								<NumericInput
									value={value}
									onChange={onChange}
									addonAfter="Days"
									min={1}
								/>
							);
						},
						editable: allowEditKeyTerms,
					},
				] : []),
			],
		},
		{
			title: 'Periods & Rates',
			content: fixture != null ? (
				<PeriodScheduleTable
					fixture={fixture}
					periodSchedule={fixture.periodSchedule}
					fixtureCurrency={fixtureCurrency}
					refreshFixture={refreshFixture}
					tcTerms={fixture.tcTerms}
				/>
			) : (<p>Period Schedule is unavailable</p>),
		},
		...(fixture.type !== FixtureTypes.BB_OUT ? [
			{
				title: 'Expenses, C/V/E & Other Revenue',
				isFilled: (
					tcTerms.ExpensesSubjectToHireDays &&
					tcTerms.ExpensesSubjectToHireDays.length > 0 &&
					tcTerms.FixtureExpenses.filter((e) => e.account === 'charterer').length > 0 &&
					tcTerms.FixtureExpenses.filter((e) => e.account === 'owner').length > 0
				),
				fields: [
					{
						key: 'calculationMethodForExpensesSubjectToHireDays',
						label: 'Calculation method for hire days',
						labelSuffix: (
							<TooltipIcon>This applies only to expenses subject to hire days</TooltipIcon>
						),
						value: tcTerms.calculationMethodForExpensesSubjectToHireDays,
						type: 'select',
						editable: true,
						required: true,
						inputProps: {
							placeholder: 'C/V/E Formula',
							style: {
								height: '55px',
							},
						},
						options: [
							{
								label: (<CVEFormulaLabel type={CalculationMethodForExpensesSubjectToHireDays.ANNUALLY} />),
								value: CalculationMethodForExpensesSubjectToHireDays.ANNUALLY,
							},
							{
								label: (<CVEFormulaLabel type={CalculationMethodForExpensesSubjectToHireDays.MONTHLY} />),
								value: CalculationMethodForExpensesSubjectToHireDays.MONTHLY,
							},
							{
								label: 'Fixed',
								value: CalculationMethodForExpensesSubjectToHireDays.FIXED,
							},
						],
					},
				],
				content: (
					<Flex vertical gap={32}>
						<CveTable
							tcTerms={tcTerms}
							refreshFixture={refreshFixture}
							currency={fixtureCurrency}
							locked={!allowEditKeyTerms}
							fixtureType={fixture.type}
						/>
						<RevenueItemsTable
							tcTerms={tcTerms}
							refreshFixture={refreshFixture}
							currency={fixtureCurrency}
							locked={!allowEditKeyTerms}
							fixtureType={fixture.type}
						/>
						<TcExpensesTables
							tcTerms={tcTerms}
							refreshFixture={refreshFixture}
							currency={fixtureCurrency}
							locked={!allowEditKeyTerms}
						/>
					</Flex>
				),
			},
		] : []),
		{
			title: 'Bunkers',
			isFilled: fixture.bunkers.length > 0,
			required: false,
			content: (
				<Space direction="vertical" className={styles.fullWidth}>
					{fixture.prevRedeliveryBunkers.length > 0 && (
						<>
							<Alert
								message="Redelivery bunkers"
								description={(
									<>
										{getRedeliveryStr()}
										<br />
										{fixture.prevRedeliveryBunkers.map((b) => {
											const bunker = b.Bunker;

											return (
												<b>
													{`- ${bunker.quantity}MT ${bunker.fuelGrade} at 
													${formatCurrency(bunker.pricePerTon, fixtureCurrency)}/MT`}
													<br />
												</b>
											);
										})}
									</>
								)}
								type="info"
								showIcon
							/>
							<Divider />
						</>
					)}
					<BunkersTable
						bunkers={fixture.bunkers}
						voyageId={fixture.voyageId}
						tcTermsId={fixture.tcTerms?.id}
						refreshDetails={refreshFixture}
						currency={fixtureCurrency}
						availableFuelGrades={fuelGrades}
						size="small"
					/>
				</Space>
			),
		},
		...(reportGroups != null ? [
			{
				title: 'Report Groups',
				fields: [
					{
						key: 'holdingReportGroup',
						label: (
							<p>
								Holding Report Group
								<TooltipIcon>
									These are set by management. If no values are present, contact management.
								</TooltipIcon>
							</p>
						),
						type: 'select',
						editable: allowEditKeyTerms,
						value: tcTerms.holdingReportGroup,
						inputProps: {
							disabled:
								reportGroups?.holdingReportGroups == null ||
								reportGroups?.holdingReportGroups?.length === 0,
							placeholder: 'Holding report group',
							showSearch: true,
						},
						options: reportGroups?.holdingReportGroups?.map((g) => ({
							value: g,
							label: g,
						})) ?? [],
						render: () => null,
					},
					{
						key: 'investorReportGroup',
						label: (
							<p>
								Investor Report Group
								<TooltipIcon>
									These are set by management. If no values are present, contact management.
								</TooltipIcon>
							</p>
						),
						type: 'select',
						editable: allowEditKeyTerms,
						value: tcTerms.investorReportGroup,
						inputProps: {
							disabled:
								reportGroups?.investorReportGroups == null ||
								reportGroups?.investorReportGroups?.length === 0,
							placeholder: 'Investor report group',
							showSearch: true,
						},
						options: reportGroups?.investorReportGroups?.map((g) => ({
							value: g,
							label: g,
						})) ?? [],
						render: () => null,
					},
					{
						key: 'clientReportGroup',
						label: (
							<p>
								Client Report Group
								<TooltipIcon>
									These are set by management. If no values are present, contact management.
								</TooltipIcon>
							</p>
						),
						type: 'select',
						editable: allowEditKeyTerms,
						value: tcTerms.clientReportGroup,
						inputProps: {
							disabled:
								reportGroups?.clientReportGroups == null ||
								reportGroups?.clientReportGroups?.length === 0,
							placeholder: 'Client report group',
							showSearch: true,
						},
						options: reportGroups?.clientReportGroups?.map((g) => ({
							value: g,
							label: g,
						})) ?? [],
						render: () => null,
					},
				],
			},
		] : []),
		{
			title: 'Notices',
			fields: [
				{
					key: 'deliveryNoticesHeader',
					label: 'Delivery',
					render: () => null,
				},
				{
					key: 'deliveryNoticesApproximate',
					label: '— Notices Approx.',
					type: 'numberList',
					value: [
						...(Array.isArray(tcTerms.deliveryNoticesApproximate) ?
							tcTerms.deliveryNoticesApproximate : []
						)].sort((a, b) => b - a),
					editable: true,
				},
				{
					key: 'deliveryNoticesDefinite',
					label: '— Notices Definite',
					type: 'numberList',
					value: [
						...(Array.isArray(tcTerms.deliveryNoticesDefinite) ?
							tcTerms.deliveryNoticesDefinite : []
						)].sort((a, b) => b - a),
					editable: true,
				},
				{
					key: 'redeliveryNoticesHeader',
					label: 'Redelivery',
					render: () => null,
				},
				{
					key: 'redeliveryNoticesApproximate',
					label: '— Notices Approx.',
					type: 'numberList',
					value: [
						...(Array.isArray(tcTerms.redeliveryNoticesApproximate) ?
							tcTerms.redeliveryNoticesApproximate : []
						)].sort((a, b) => b - a),
					editable: true,
				},
				{
					key: 'redeliveryNoticesDefinite',
					label: '— Notices Definite',
					type: 'numberList',
					value: [
						...(Array.isArray(tcTerms.redeliveryNoticesDefinite) ?
							tcTerms.redeliveryNoticesDefinite : []
						)].sort((a, b) => b - a),
					editable: true,
				},
			],
		},
		{
			title: 'Notes',
			fields: [
				getNotesField({ fixture, required: false }),
			],
		},
	];
};
